import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  EnumBookerEligibilityIneligibleBehavior,
  EnumBookerEligibilityMode,
  EnumDiscountUnit,
  EnumProductType,
  Product,
  useBookerCartPriceLazyQuery,
  useBookerCheckEligibilityQuery,
  useDiscountByIdsQuery,
  useRenewableFeeEarlyRenewalsQuery,
  useRenewableFeeQuoteQuery,
} from "gql/generated";
import { StripeCardElement, StripeCardNumberElement } from "@stripe/stripe-js";
import { useBookerContext } from "../BookerContextProvider";
import {
  adjustCartPricing,
  buildInitialGuestsPerTierForProduct,
  isValidTaxExemptionNumber,
  splitGuestName,
} from "./helpers";
import { rescaleRenewableCartTotals } from "./rescaleRenewableCartTotals";
import { PostHogCustomEvent } from "shared/hooks/usePostHog";
import { useWidgetTrack } from "widgets/shared/WidgetTrackingContext";
import {
  getMultilingualString,
  getResolvedProductSellingConfig,
  calculateProRatedAmount,
  formatToISO8601,
} from "shared/utils";
import { useOrgScopedUserOperators } from "widgets/booker/shared/hooks/useOrgScopedUserOperators";
import { parseCartSnapshotCustomerAddress } from "shared/components/organisms/SurveyQuestionInput/addressSearchResponse";
import {
  BusinessFieldValue,
  CartItem,
  CustomerAddressValue,
  CustomerContact,
  InvoiceAccommodationDraft,
  QuestionnaireResponse,
  getBusinessFieldOperatorIds,
} from "./cartContextTypes";
import { CartSnapshot } from "./cartSnapshotTypes";
import { CartContext, CartContextValues } from "./CartContext";

const defaultValues = {
  customer: {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    complete: false,
  },
  totals: {
    subTotal: 0,
    tax: 0,
    taxLines: [] as {
      name: string;
      ratePercent?: number | null;
      amount: number;
    }[],
    grandTotal: 0,
    depositTotal: 0,
    customerFee: 0,
  },
  cart: [],
  discounts: [],
  stripeCardElement: undefined,
  cardPostalCode: "",
  paymentMethodId: "",
  acceptedPolicies: false,
  guestNames: [], // Start with an empty array (user needs to select a count first)
  promoCode: "",
  questionnaireComplete: false,
  questionnaireResponses: [],
  taxExempt: false,
  taxExemptionNumber: "",
  autoRenewOptIn: false,
  messageFromGuest: "",
};

export const CartContextProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const cartPricingGenRef = useRef(0);
  const cartRef = useRef<CartItem[]>(defaultValues.cart);
  const invoiceAccommodationDraftGetterRef = useRef<
    (() => InvoiceAccommodationDraft) | null
  >(null);

  const setInvoiceAccommodationDraftGetter = useCallback(
    (getter: (() => InvoiceAccommodationDraft) | null) => {
      invoiceAccommodationDraftGetterRef.current = getter;
    },
    [],
  );

  const getInvoiceAccommodationDraft = useCallback(
    () => invoiceAccommodationDraftGetterRef.current?.() ?? null,
    [],
  );

  const {
    product,
    booker,
    isEmbedded,
    language,
    defaultContact,
    userOperators,
    organizationId,
    initialCartSnapshot,
    readOnlySelection,
    broadcastId,
    paymentRequestId,
  } = useBookerContext();

  const isTeamVisible =
    (booker as { teamVisible?: boolean })?.teamVisible === true;
  const { operators: orgScopedOperators, loading: orgScopedOperatorsLoading } =
    useOrgScopedUserOperators(
      userOperators,
      organizationId,
      isTeamVisible && !!organizationId,
    );
  // Memoised so its reference stays stable across renders. Without this,
  // `userOperators ?? []` produces a fresh array each render, which destabilises
  // `resetCartValuesToDefault` and causes the reset-on-open effect in
  // SingleProductModal to re-fire on every render, wiping the cart 50ms after
  // every interaction (most visible on events, which aren't auto re-added).
  const effectiveUserOperators = useMemo(
    () =>
      organizationId && isTeamVisible
        ? orgScopedOperators
        : (userOperators ?? []),
    [organizationId, isTeamVisible, orgScopedOperators, userOperators],
  );

  // A phone is only needed for completeness when the widget actually
  // collects one; otherwise a locked invoice recipient without a phone could
  // never complete the (fully disabled) customer step.
  const phoneCollected = booker?.customerFields?.phoneEnabled ?? true;
  const lockedCustomer = useMemo(
    () => ({
      firstName: defaultContact?.firstName ?? "",
      lastName: defaultContact?.lastName ?? "",
      email: defaultContact?.email ?? "",
      phone: defaultContact?.phone ?? "",
      complete: !!(
        defaultContact?.firstName &&
        defaultContact?.lastName &&
        defaultContact?.email &&
        (defaultContact?.phone || !phoneCollected)
      ),
    }),
    [defaultContact, phoneCollected],
  );

  const defaultCustomerAddress = useMemo(
    () => defaultContact?.address ?? null,
    [defaultContact?.address],
  );

  const [cart, setCart] = useState<CartContextValues["cart"]>(
    defaultValues.cart,
  );
  const [customer, setCustomer] = useState<CartContextValues["customer"]>(
    () => ({
      ...defaultValues.customer,
      ...lockedCustomer,
    }),
  );
  const [totals, setTotals] = useState(defaultValues.totals);
  const [delivery, setDelivery] = useState<CartContextValues["delivery"]>(null);
  const deliveryRef = useRef<CartContextValues["delivery"]>(null);
  const [shipping, setShipping] = useState<{
    total: number;
    available: boolean | null;
  }>({ total: 0, available: null });
  const [discounts, setDiscounts] = useState<CartContextValues["discounts"]>(
    defaultValues.discounts,
  );
  const [promoCode, setPromoCode] = useState<CartContextValues["promoCode"]>(
    defaultValues.promoCode,
  );
  const [promoCodeError, setPromoCodeError] = useState(false);
  const [stripeCardElement, setStripeCardElement] = useState<
    CartContextValues["stripeCardElement"]
  >(defaultValues.stripeCardElement);
  const [cardPostalCode, setCardPostalCode] = useState<
    CartContextValues["cardPostalCode"]
  >(defaultValues.cardPostalCode);
  const [paymentMethodId, setPaymentMethodId] = useState<
    CartContextValues["paymentMethodId"]
  >(defaultValues.paymentMethodId);
  const [acceptedPolicies, setAcceptedPolicies] = useState<
    CartContextValues["acceptedPolicies"]
  >(defaultValues.acceptedPolicies);
  const [guestNames, setGuestNames] = useState<CartContextValues["guestNames"]>(
    defaultValues.guestNames,
  );
  const [eventGuestDetails, setEventGuestDetails] = useState<
    CartContextValues["eventGuestDetails"]
  >([]);
  /** True after `hydrateSnapshot` — cart line totals are frozen and must not be re-pro-rated. */
  const [cartHydratedFromSnapshot, setCartHydratedFromSnapshot] =
    useState(false);
  const [questionnaireComplete, setQuestionnaireComplete] = useState<
    CartContextValues["questionnaireComplete"]
  >(defaultValues.questionnaireComplete);
  const [questionnaireResponses, setQuestionnaireResponses] = useState<
    CartContextValues["questionnaireResponses"]
  >(defaultValues.questionnaireResponses);
  const [taxExempt, setTaxExempt] = useState<CartContextValues["taxExempt"]>(
    defaultValues.taxExempt,
  );
  const [taxExemptionNumber, setTaxExemptionNumber] = useState<
    CartContextValues["taxExemptionNumber"]
  >(defaultValues.taxExemptionNumber);
  const [autoRenewOptIn, setAutoRenewOptIn] = useState<
    CartContextValues["autoRenewOptIn"]
  >(defaultValues.autoRenewOptIn);
  const [messageFromGuest, setMessageFromGuest] = useState(
    defaultValues.messageFromGuest,
  );

  // Tax exemption is only priced (tax zeroed) once a valid exemption number
  // has been entered — toggling the switch alone never removes tax.
  const effectiveTaxExempt =
    taxExempt && isValidTaxExemptionNumber(taxExemptionNumber);

  const [selectedBillingInterval, setSelectedBillingInterval] = useState<
    string | null
  >(null);

  const [selectedTierId, setSelectedTierId] = useState<string | null>(null);

  const [selectedOperatorId, setSelectedOperatorId] = useState<string | null>(
    null,
  );
  const [businessField, setBusinessField] = useState<BusinessFieldValue>({});
  const [customerAddress, setCustomerAddress] = useState<CustomerAddressValue>(
    () => defaultCustomerAddress,
  );
  const [snapshotToPrice, setSnapshotToPrice] = useState<CartSnapshot | null>(
    null,
  );

  useEffect(() => {
    if (orgScopedOperatorsLoading) return;
    if (effectiveUserOperators.length === 1 && !selectedOperatorId) {
      setSelectedOperatorId(effectiveUserOperators[0].entityId);
    }
  }, [effectiveUserOperators, orgScopedOperatorsLoading, selectedOperatorId]);

  const [getCartPrices] = useBookerCartPriceLazyQuery();

  cartRef.current = cart;

  const runAdjustCartPricing = useCallback(
    (
      newCart: CartItem[],
      opts: {
        promoCode: string;
        mainProductPricing: Product["pricing"];
        selectedTierId?: string | null;
        taxExempt?: boolean;
      },
    ) => {
      const requestGen = ++cartPricingGenRef.current;
      adjustCartPricing({
        getCartPrices,
        bookerID: booker?._id ?? "",
        setCart,
        setTotals,
        setDiscounts,
        newCart,
        promoCode: opts.promoCode,
        setPromoCode,
        setPromoCodeError,
        mainProductPricing: opts.mainProductPricing,
        selectedTierId: opts.selectedTierId,
        taxExempt: opts.taxExempt,
        delivery: deliveryRef.current,
        setShipping,
        isStale: () => requestGen !== cartPricingGenRef.current,
      });
    },
    [booker?._id, getCartPrices, setCart, setTotals, setDiscounts],
  );

  const trackPostHogEvent = useWidgetTrack();
  const resolvedSellingConfig = useMemo(
    () =>
      getResolvedProductSellingConfig(
        product as typeof product & { sellingConfig?: any },
      ),
    [product],
  );

  const resetCartValuesToDefault = useCallback(() => {
    cartPricingGenRef.current = 0;
    invoiceAccommodationDraftGetterRef.current = null;
    setSnapshotToPrice(null);
    setCart(defaultValues.cart);
    setCustomer({
      ...defaultValues.customer,
      ...lockedCustomer,
    });
    setTotals(defaultValues.totals);
    setStripeCardElement(defaultValues.stripeCardElement);
    setCardPostalCode(defaultValues.cardPostalCode);
    setPaymentMethodId(defaultValues.paymentMethodId);
    setAcceptedPolicies(defaultValues.acceptedPolicies);
    setGuestNames(defaultValues.guestNames);
    setEventGuestDetails([]);
    setDiscounts(defaultValues.discounts);
    setQuestionnaireComplete(defaultValues.questionnaireComplete);
    setQuestionnaireResponses(defaultValues.questionnaireResponses);
    setTaxExempt(defaultValues.taxExempt);
    setTaxExemptionNumber(defaultValues.taxExemptionNumber);
    setAutoRenewOptIn(defaultValues.autoRenewOptIn);
    setMessageFromGuest(defaultValues.messageFromGuest);
    setSelectedTierId(null);
    setSelectedBillingInterval(null);
    setSelectedOperatorId(
      effectiveUserOperators.length === 1
        ? effectiveUserOperators[0].entityId
        : null,
    );
    setBusinessField({});
    setCustomerAddress(defaultCustomerAddress);
    setCartHydratedFromSnapshot(false);
    deliveryRef.current = null;
    setDelivery(null);
    setShipping({ total: 0, available: null });
  }, [lockedCustomer, effectiveUserOperators, defaultCustomerAddress]);

  const updatePaymentMethodId = useCallback(
    (id: string) => {
      setPaymentMethodId(id);

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.PaymentMethodAdded, {
          bookerId: booker?._id,
        });
      }
    },
    [booker?._id, isEmbedded, trackPostHogEvent],
  );

  const hydrateSnapshot = useCallback(
    (rawSnapshot: CartSnapshot) => {
      // Snapshots built from booked orders may carry inventory-claim UTC
      // timestamps; the calendar only accepts yyyy-MM-dd.
      const toCalendarDate = (value: string) =>
        value?.includes("T") ? (formatToISO8601(value) as string) : value;
      const snapshot: CartSnapshot = {
        ...rawSnapshot,
        cart: Array.isArray(rawSnapshot.cart)
          ? rawSnapshot.cart.map(item => ({
              ...item,
              startDateISO: toCalendarDate(item.startDateISO),
              endDateISO: toCalendarDate(item.endDateISO),
            }))
          : defaultValues.cart,
      };
      setCartHydratedFromSnapshot(true);
      setSnapshotToPrice(snapshot);
      setCart(snapshot.cart);
      setCustomer({
        ...defaultValues.customer,
        ...snapshot.customer,
        ...(readOnlySelection ? lockedCustomer : {}),
        complete: readOnlySelection
          ? lockedCustomer.complete
          : (snapshot.customerComplete ??
            snapshot.customer?.complete ??
            defaultValues.customer.complete),
      });
      setTotals(defaultValues.totals);
      setDiscounts(defaultValues.discounts);
      setStripeCardElement(defaultValues.stripeCardElement);
      setCardPostalCode(defaultValues.cardPostalCode);
      setPaymentMethodId(defaultValues.paymentMethodId);
      setAcceptedPolicies(snapshot.acceptedPolicies ?? false);
      setGuestNames(snapshot.guestNames ?? defaultValues.guestNames);
      // Prefer the structured `guests` (name + email); fall back to
      // synthesizing name-only entries from legacy `guestNames` so invoices
      // composed before this field existed still restore guest names.
      setEventGuestDetails(
        snapshot.guests?.length
          ? snapshot.guests.map(g => ({
              name: g.name ?? "",
              email: g.email ?? "",
            }))
          : (snapshot.guestNames ?? []).map(name => ({ name, email: "" })),
      );
      setPromoCode(snapshot.promoCode ?? defaultValues.promoCode);
      setQuestionnaireComplete(
        snapshot.questionnaireComplete ?? defaultValues.questionnaireComplete,
      );
      setQuestionnaireResponses(
        snapshot.questionnaireResponses ?? defaultValues.questionnaireResponses,
      );
      setTaxExempt(snapshot.taxExempt ?? defaultValues.taxExempt);
      setTaxExemptionNumber(
        snapshot.taxExemptionNumber ?? defaultValues.taxExemptionNumber,
      );
      setSelectedBillingInterval(snapshot.selectedBillingInterval ?? null);
      setSelectedTierId(snapshot.selectedTierId ?? null);
      setSelectedOperatorId(
        snapshot.selectedOperatorId ??
          (effectiveUserOperators.length === 1
            ? effectiveUserOperators[0].entityId
            : null),
      );
      setBusinessField(snapshot.businessField ?? {});
      setCustomerAddress(
        parseCartSnapshotCustomerAddress(snapshot.customerAddress) ??
          defaultCustomerAddress,
      );
    },
    [
      lockedCustomer,
      readOnlySelection,
      effectiveUserOperators,
      defaultCustomerAddress,
    ],
  );

  const acceptPolicies = useCallback(() => {
    setAcceptedPolicies(true);

    if (isEmbedded) {
      trackPostHogEvent(PostHogCustomEvent.PoliciesAccepted, {
        bookerId: booker?._id,
      });
    }
  }, [booker?._id, isEmbedded, trackPostHogEvent]);

  const updateStripeCardElement = (
    card?: StripeCardElement | StripeCardNumberElement,
  ) => {
    setStripeCardElement(card);
  };

  const updateCardPostalCode = (postalCode: string) => {
    setCardPostalCode(postalCode);
  };

  const updateCustomer = useCallback(
    (cust: CustomerContact) => {
      setCustomer(cust);

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.CustomerDetailsAdded, {
          bookerId: booker?._id,
        });
      }
    },
    [booker?._id, isEmbedded, trackPostHogEvent],
  );

  const incompleteCustomer = (cust?: Partial<CustomerContact>) => {
    setCustomer(prev => ({ ...prev, ...cust, complete: false }));
  };

  const addCartItem = useCallback(
    (cartItem: CartItem) => {
      const newCart = [...cartRef.current, cartItem];

      runAdjustCartPricing(newCart, {
        promoCode,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: effectiveTaxExempt,
      });

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.ProductAddedToCart, {
          bookerId: booker?._id,
          productId: cartItem.productId,
        });
      }
    },
    [
      booker?._id,
      isEmbedded,
      promoCode,
      runAdjustCartPricing,
      trackPostHogEvent,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  const updateCartItem = useCallback(
    (productId: string, updatedProps: Partial<CartItem>) => {
      const newCart = cartRef.current.map(item => {
        if (item.productId === productId) {
          return { ...item, ...updatedProps };
        }
        return item;
      });

      runAdjustCartPricing(newCart, {
        promoCode,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: effectiveTaxExempt,
      });

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.ProductUpdatedInCart, {
          bookerId: booker?._id,
          productId: productId,
        });
      }
    },
    [
      booker?._id,
      isEmbedded,
      promoCode,
      runAdjustCartPricing,
      trackPostHogEvent,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  /** Answers don't affect price, so update in place without a pricing round-trip. */
  const updateCartItemDetails = useCallback(
    (
      productId: string,
      customFieldResponses: CartItem["customFieldResponses"],
      businessScopes?: CartItem["businessScopes"],
    ) => {
      setCart(current =>
        current.map(item =>
          item.productId === productId
            ? {
                ...item,
                customFieldResponses,
                ...(businessScopes !== undefined ? { businessScopes } : {}),
              }
            : item,
        ),
      );
    },
    [],
  );

  const removeCartItem = useCallback(
    (productId: string) => {
      const newCart = cartRef.current.filter(
        item => item.productId !== productId,
      );

      runAdjustCartPricing(newCart, {
        promoCode,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: effectiveTaxExempt,
      });

      if (isEmbedded) {
        trackPostHogEvent(PostHogCustomEvent.ProductRemovedFromCart, {
          bookerId: booker?._id,
          productId: productId,
        });
      }
    },
    [
      booker?._id,
      isEmbedded,
      promoCode,
      runAdjustCartPricing,
      trackPostHogEvent,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  const updateGuestNames = (names: string[]) => {
    setGuestNames(names);
  };

  const updateEventGuestDetails = (
    details: { name: string; email: string }[],
  ) => {
    setEventGuestDetails(details);
  };

  const updatePromoCode = useCallback(
    (code: string) => {
      setPromoCode(code);
      setPromoCodeError(false);

      runAdjustCartPricing(cartRef.current, {
        promoCode: code,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: effectiveTaxExempt,
      });

      if (isEmbedded && code.length > 0) {
        trackPostHogEvent(PostHogCustomEvent.PromoCodeApplied, {
          bookerId: booker?._id,
          promoCode: code,
        });
      }
    },
    [
      booker?._id,
      isEmbedded,
      runAdjustCartPricing,
      trackPostHogEvent,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  const updateSelectedTier = useCallback(
    (tierId: string) => {
      setSelectedTierId(tierId);

      if (cartRef.current.length > 0 && product?._id) {
        runAdjustCartPricing(cartRef.current, {
          promoCode,
          mainProductPricing: product.pricing,
          selectedTierId: tierId,
          taxExempt: effectiveTaxExempt,
        });
      }
    },
    [booker?._id, product, promoCode, runAdjustCartPricing, effectiveTaxExempt],
  );

  const updateDelivery = useCallback(
    (next: CartContextValues["delivery"]) => {
      deliveryRef.current = next;
      setDelivery(next);
      if (cartRef.current.length === 0) return;
      runAdjustCartPricing(cartRef.current, {
        promoCode,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: effectiveTaxExempt,
      });
    },
    [
      runAdjustCartPricing,
      promoCode,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  const updateTaxExemption = useCallback(
    (exempt: boolean, exemptionNumber: string) => {
      setTaxExempt(exempt);
      setTaxExemptionNumber(exemptionNumber);

      // Only reprice when the exemption actually starts/stops applying: the
      // number is required, so tax is not zeroed until a valid one is entered
      // (and typing keystrokes that don't change validity don't refetch).
      const nextEffective =
        exempt && isValidTaxExemptionNumber(exemptionNumber);
      if (nextEffective === effectiveTaxExempt) return;

      runAdjustCartPricing(cartRef.current, {
        promoCode,
        mainProductPricing: product?.pricing,
        selectedTierId,
        taxExempt: nextEffective,
      });
    },
    [
      runAdjustCartPricing,
      promoCode,
      product?.pricing,
      selectedTierId,
      effectiveTaxExempt,
    ],
  );

  const completeQuestionnaire = useCallback(() => {
    setQuestionnaireComplete(true);

    if (isEmbedded) {
      trackPostHogEvent(PostHogCustomEvent.QuestionnaireCompleted, {
        bookerId: booker?._id,
      });
    }
  }, [booker?._id, isEmbedded, trackPostHogEvent]);

  const setQuestionnaireCompleteState = useCallback(
    (complete: boolean) => {
      if (complete) completeQuestionnaire();
      else setQuestionnaireComplete(false);
    },
    [completeQuestionnaire],
  );

  const updateQuestionnaireResponses = useCallback(
    (
      responses:
        | QuestionnaireResponse[]
        | ((current: QuestionnaireResponse[]) => QuestionnaireResponse[]),
    ) => {
      setQuestionnaireResponses(responses);
    },
    [],
  );

  useEffect(() => {
    if (!snapshotToPrice) return;
    if (!booker?._id) return;

    runAdjustCartPricing(snapshotToPrice.cart, {
      promoCode: snapshotToPrice.promoCode ?? "",
      mainProductPricing: product?.pricing,
      selectedTierId: snapshotToPrice.selectedTierId,
      taxExempt:
        !!snapshotToPrice.taxExempt &&
        isValidTaxExemptionNumber(snapshotToPrice.taxExemptionNumber ?? ""),
    });

    setSnapshotToPrice(null);
  }, [booker?._id, product?.pricing, runAdjustCartPricing, snapshotToPrice]);

  useEffect(() => {
    if (!initialCartSnapshot) return;
    hydrateSnapshot(initialCartSnapshot);
  }, [hydrateSnapshot, initialCartSnapshot]);

  /**
   * When an event collects guest names, treat the first guest as the
   * purchaser and keep the customer details step in sync with them, so the
   * buyer doesn't have to re-type their own info. This is skipped inside a
   * broadcast, where the purchaser is already a known, signed-in person
   * (surfaced via `defaultContact`) who should keep filling their own info,
   * and it never overwrites a contact that's already locked in from
   * elsewhere (invoice/payment-request recipients) or one the customer has
   * already submitted.
   */
  const isEventProduct = product?.type === EnumProductType.Event;
  const guestNamesEnabled = booker?.customerFields?.guestNamesEnabled ?? false;
  const isBroadcastEmbed = !!broadcastId;
  const firstGuestName = eventGuestDetails[0]?.name ?? "";
  const firstGuestEmail = eventGuestDetails[0]?.email ?? "";

  useEffect(() => {
    if (!isEventProduct || !guestNamesEnabled || isBroadcastEmbed) return;
    if (readOnlySelection || lockedCustomer.complete) return;
    if (!firstGuestName.trim()) return;

    const { firstName, lastName } = splitGuestName(firstGuestName);
    setCustomer(prev => {
      if (prev.complete) return prev;
      if (
        prev.firstName === firstName &&
        prev.lastName === lastName &&
        prev.email === (firstGuestEmail || prev.email)
      ) {
        return prev;
      }
      return {
        ...prev,
        firstName,
        lastName,
        email: firstGuestEmail || prev.email,
      };
    });
  }, [
    isEventProduct,
    guestNamesEnabled,
    isBroadcastEmbed,
    readOnlySelection,
    lockedCustomer.complete,
    firstGuestName,
    firstGuestEmail,
  ]);

  /** Initialize the cart with the main product (merch items are added individually by the user) */
  useEffect(() => {
    if (product && !cart.find(item => item.productId === product._id)) {
      const isMainProductFee = product.type === EnumProductType.Fee;
      const isRenewableFee = product.type === EnumProductType.RenewableFee;
      const isEvent = product.type === EnumProductType.Event;
      const isMerch = product.type === EnumProductType.Merch;
      const isOneTimeSellable = isMainProductFee || isMerch || isRenewableFee;

      if (isMerch || isEvent) return;

      const guestsPerTier = buildInitialGuestsPerTierForProduct(product);
      // If the product is a fee, we need to set the total to the price of the
      // first tier as if they are automatically purchasing 1 quantity of the fee product
      addCartItem({
        productId: product._id,
        productName: getMultilingualString(product.name, language) ?? "",
        productType: product.type,
        endDateISO: "",
        startDateISO: "",
        guestsPerTier,
        total:
          isOneTimeSellable && resolvedSellingConfig.options.length === 0
            ? resolvedSellingConfig.basePrice
            : 0,
        quantity: 1,
      });
    }
  }, [product, addCartItem, cart, language, resolvedSellingConfig]);

  /**
   * Reprice when a tier is selected but the cart was already populated (or the
   * cart was just initialised) with a $0 total because selectedTierId was null
   * at the time addCartItem / adjustCartPricing first ran.
   */
  useEffect(() => {
    if (!selectedTierId) return;
    if (!booker?._id || !product?._id) return;
    if (cart.length === 0) return;
    if (totals.subTotal > 0) return;

    runAdjustCartPricing(cartRef.current, {
      promoCode,
      mainProductPricing: product.pricing,
      selectedTierId,
      taxExempt: effectiveTaxExempt,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedTierId,
    cart.length,
    booker?._id,
    product?._id,
    runAdjustCartPricing,
    promoCode,
    effectiveTaxExempt,
  ]);

  const isRenewableFee = product?.type === EnumProductType.RenewableFee;
  const recurringForProRate = resolvedSellingConfig.recurring;
  const usesFixedDateProRate =
    isRenewableFee &&
    !cartHydratedFromSnapshot &&
    !!recurringForProRate?.proRateEnabled &&
    recurringForProRate.renewalType === "FIXED_DATE" &&
    !!recurringForProRate.fixedRenewalMonth &&
    !!recurringForProRate.fixedRenewalDay;

  // Once we know who is buying, the server decides between pro-rated, early
  // purchase and renewal pricing (it can see their membership); the local
  // date-only calculation can't tell a renewing member from a new joiner.
  const quoteOperatorId =
    businessField?.operatorEntityId || selectedOperatorId || undefined;
  const quoteOperatorIds = getBusinessFieldOperatorIds(businessField);
  // The cart prices one listing; the server prices every included business.
  const usesMultiBusinessQuote =
    isRenewableFee && !cartHydratedFromSnapshot && quoteOperatorIds.length > 1;
  const quoteActive =
    (usesFixedDateProRate || usesMultiBusinessQuote) &&
    !paymentRequestId &&
    !!booker?._id &&
    !!product?._id &&
    customer.complete &&
    !!(customer.email || quoteOperatorId);
  const quoteQuery = useRenewableFeeQuoteQuery({
    skip: !quoteActive,
    variables: {
      bookerId: booker?._id,
      productId: product?._id,
      tierId: selectedTierId || undefined,
      organizationId: booker?.storefront,
      customerEmail: customer.email || undefined,
      operatorId: quoteOperatorId,
      operatorIds:
        quoteOperatorIds.length > 1 ? quoteOperatorIds : undefined,
    },
    fetchPolicy: "cache-and-network",
  });
  const quoteData = quoteActive ? quoteQuery.data?.renewableFeeQuote : null;
  const renewableFeeQuote = useMemo<CartContextValues["renewableFeeQuote"]>(
    () => ({
      active: quoteActive,
      loading: quoteActive && !quoteData && quoteQuery.loading,
      error: quoteActive && !quoteData && !!quoteQuery.error,
      data: quoteData ?? null,
    }),
    [quoteActive, quoteData, quoteQuery.loading, quoteQuery.error],
  );

  const isMembersOnly =
    booker?.eligibility?.mode === EnumBookerEligibilityMode.VerifiedMembers;
  const eligibilityActive =
    isMembersOnly &&
    !isRenewableFee &&
    !paymentRequestId &&
    !!booker?._id &&
    customer.complete;
  const eligibilityQuery = useBookerCheckEligibilityQuery({
    skip: !eligibilityActive,
    variables: {
      bookerId: booker?._id,
      email: customer.email || undefined,
      phone: customer.phone || undefined,
    },
    fetchPolicy: "network-only",
  });
  const eligibilityData = eligibilityActive
    ? eligibilityQuery.data?.bookerCheckEligibility
    : null;
  const customerEligibility = useMemo<CartContextValues["customerEligibility"]>(
    () => ({
      active: eligibilityActive,
      loading: eligibilityActive && eligibilityQuery.loading,
      eligible: isMembersOnly ? !!eligibilityData?.eligible : true,
      blocked:
        !!eligibilityData &&
        !eligibilityData.eligible &&
        eligibilityData.ineligibleBehavior ===
          EnumBookerEligibilityIneligibleBehavior.Block,
      blockedMessage: eligibilityData?.blockedMessage ?? null,
    }),
    [isMembersOnly, eligibilityActive, eligibilityQuery.loading, eligibilityData],
  );

  const businessFieldEnabled = !!booker?.customerFields?.businessFieldEnabled;
  const { operators: businessFieldOperators } = useOrgScopedUserOperators(
    userOperators,
    organizationId,
    isRenewableFee && businessFieldEnabled && !!organizationId,
  );
  const selectedMembershipOperatorKey = (
    quoteOperatorIds.length > 0
      ? quoteOperatorIds
      : quoteOperatorId
        ? [quoteOperatorId]
        : []
  ).join(",");
  const selectedMembershipOperatorIds = useMemo(
    () =>
      selectedMembershipOperatorKey
        ? selectedMembershipOperatorKey.split(",")
        : [],
    [selectedMembershipOperatorKey],
  );
  const earlyRenewalOperatorIds = useMemo(
    () => [
      ...new Set([
        ...businessFieldOperators.map(op => op.entityId),
        ...selectedMembershipOperatorIds,
      ]),
    ],
    [businessFieldOperators, selectedMembershipOperatorIds],
  );
  const earlyRenewalEmail = customer.email || defaultContact?.email || "";
  const earlyRenewalActive =
    isRenewableFee &&
    !paymentRequestId &&
    !!booker?._id &&
    !!product?._id &&
    (earlyRenewalOperatorIds.length > 0 || !!earlyRenewalEmail);
  const earlyRenewalQuery = useRenewableFeeEarlyRenewalsQuery({
    skip: !earlyRenewalActive,
    variables: {
      bookerId: booker?._id,
      productId: product?._id,
      organizationId: booker?.storefront?.organization ?? booker?.storefront,
      tierId: selectedTierId || undefined,
      operatorIds:
        earlyRenewalOperatorIds.length > 0 ? earlyRenewalOperatorIds : undefined,
      customerEmail: earlyRenewalEmail || undefined,
    },
    fetchPolicy: "cache-and-network",
  });
  const earlyRenewals = useMemo<CartContextValues["earlyRenewals"]>(() => {
    const entries = earlyRenewalActive
      ? (earlyRenewalQuery.data?.renewableFeeEarlyRenewals ?? [])
      : [];
    return {
      loading: earlyRenewalActive && earlyRenewalQuery.loading,
      entries,
      blocking: entries.filter(entry =>
        entry.operatorId
          ? selectedMembershipOperatorIds.includes(entry.operatorId)
          : selectedMembershipOperatorIds.length === 0,
      ),
    };
  }, [
    earlyRenewalActive,
    earlyRenewalQuery.data,
    earlyRenewalQuery.loading,
    selectedMembershipOperatorIds,
  ]);

  const proRateRatio = useMemo(() => {
    if (quoteActive) {
      // Loading or failed: show full price rather than a guess.
      if (!quoteData || quoteData.fullAmount <= 0) return 1;
      const singleListingAmount = quoteData.tierAmount || quoteData.fullAmount;
      return quoteData.chargeAmount / singleListingAmount;
    }
    if (!usesFixedDateProRate) return 1;
    const basePrice = resolvedSellingConfig.basePrice || totals.subTotal;
    if (basePrice <= 0) return 1;
    const proRated = calculateProRatedAmount(basePrice, recurringForProRate);
    return proRated / basePrice;
  }, [
    usesFixedDateProRate,
    quoteActive,
    quoteData,
    resolvedSellingConfig.basePrice,
    recurringForProRate,
    totals.subTotal,
  ]);

  const { data: cartDiscountDocs } = useDiscountByIdsQuery({
    variables: { ids: discounts.map(d => d.discountId) },
    skip: proRateRatio === 1 || discounts.length === 0,
  });

  // The cart is priced for one business at full price. Pro-ration and
  // multi-business pricing scale only the membership row; add-ons stay at face
  // value. Discounts are then re-applied to that base the way
  // `renewableFeeInitiate` charges them: percentages follow the new base,
  // fixed amounts stay fixed.
  const { adjustedTotals, adjustedDiscounts } = useMemo(
    () =>
      rescaleRenewableCartTotals({
        cart,
        ratio: proRateRatio,
        totals,
        discounts,
        percentageDiscountIds: new Set(
          (cartDiscountDocs?.discountByIds ?? [])
            .filter(d => d.unit === EnumDiscountUnit.Percentage)
            .map(d => String(d._id)),
        ),
      }),
    [cart, proRateRatio, totals, discounts, cartDiscountDocs],
  );

  const adjustedCart = useMemo(() => {
    if (proRateRatio === 1) return cart;
    return cart.map((item, index) =>
      index === 0
        ? { ...item, total: Math.round(item.total * proRateRatio) }
        : item,
    );
  }, [cart, proRateRatio]);

  const contextValue: CartContextValues = useMemo(
    () => ({
      cart: adjustedCart,
      customer,
      stripeCardElement,
      cardPostalCode,
      paymentMethodId,
      acceptedPolicies,
      questionnaireComplete,
      questionnaireResponses,
      subTotal: adjustedTotals.subTotal,
      tax: adjustedTotals.tax,
      taxLines: adjustedTotals.taxLines,
      grandTotal: adjustedTotals.grandTotal,
      depositTotal: adjustedTotals.depositTotal,
      customerFee: adjustedTotals.customerFee,
      delivery,
      updateDelivery,
      shippingTotal: shipping.total,
      deliveryAvailable: shipping.available,
      discounts: adjustedDiscounts,
      addCartItem,
      updateCartItem,
      updateCartItemDetails,
      removeCartItem,
      updateCustomer,
      incompleteCustomer,
      updateStripeCardElement,
      updateCardPostalCode,
      updatePaymentMethodId,
      acceptPolicies,
      guestNames,
      updateGuestNames,
      eventGuestDetails,
      updateEventGuestDetails,
      resetCartValuesToDefault,
      promoCode,
      promoCodeError,
      updatePromoCode,
      taxExempt,
      taxExemptionNumber,
      effectiveTaxExempt,
      updateTaxExemption,
      autoRenewOptIn,
      updateAutoRenewOptIn: setAutoRenewOptIn,
      messageFromGuest,
      updateMessageFromGuest: setMessageFromGuest,
      completeQuestionnaire,
      setQuestionnaireCompleteState,
      updateQuestionnaireResponses,
      selectedBillingInterval,
      updateBillingInterval: setSelectedBillingInterval,
      selectedTierId,
      updateSelectedTier,
      selectedOperatorId,
      updateOperatorId: setSelectedOperatorId,
      businessField,
      updateBusinessField: setBusinessField,
      customerAddress,
      updateCustomerAddress: setCustomerAddress,
      hydrateSnapshot,
      setInvoiceAccommodationDraftGetter,
      getInvoiceAccommodationDraft,
      renewableFeeQuote,
      customerEligibility,
      earlyRenewals,
    }),
    [
      adjustedCart,
      customer,
      stripeCardElement,
      cardPostalCode,
      paymentMethodId,
      acceptedPolicies,
      questionnaireComplete,
      questionnaireResponses,
      adjustedTotals.subTotal,
      adjustedTotals.tax,
      adjustedTotals.taxLines,
      adjustedTotals.grandTotal,
      adjustedTotals.depositTotal,
      adjustedTotals.customerFee,
      delivery,
      updateDelivery,
      shipping,
      adjustedDiscounts,
      addCartItem,
      updateCartItem,
      updateCartItemDetails,
      removeCartItem,
      updateCustomer,
      updatePaymentMethodId,
      acceptPolicies,
      guestNames,
      eventGuestDetails,
      resetCartValuesToDefault,
      promoCode,
      promoCodeError,
      updatePromoCode,
      taxExempt,
      taxExemptionNumber,
      effectiveTaxExempt,
      updateTaxExemption,
      autoRenewOptIn,
      messageFromGuest,
      completeQuestionnaire,
      setQuestionnaireCompleteState,
      updateQuestionnaireResponses,
      selectedBillingInterval,
      selectedTierId,
      updateSelectedTier,
      selectedOperatorId,
      businessField,
      hydrateSnapshot,
      customerAddress,
      setInvoiceAccommodationDraftGetter,
      getInvoiceAccommodationDraft,
      renewableFeeQuote,
      customerEligibility,
      earlyRenewals,
    ],
  );

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};
